import React, { useEffect, useMemo, useState } from 'react';
import { Shield, Zap, Award, Moon, Sun, TrendingUp, BarChart3, Globe, Smartphone } from 'lucide-react';

// ===== API base: change with env var in dev if needed
const API = process.env.NODE_ENV === 'development' ? '' : 'https://gheymatbazar.com';

// ===== helpers =====
const pct = (v) => (v === 0 || v === '0' || v === null || v === undefined || isNaN(Number(v))) ? '0%' : `${Number(v).toFixed(2)}%`;
const pos = (v) => Number(v) >= 0;
const pick = (obj, key) => obj?.[key];
const pick3 = (rec, keys, mapName = (k) => k) =>
  keys
    .filter((k) => rec && rec[k])
    .slice(0, 3)
    .map((k) => ({
      name: mapName(k),
      price: rec[k].price,
      change: Number(rec[k].percent_change),
      positive: pos(rec[k].percent_change),
    }));

// ========== Theme toggle ==========
const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  const saved = localStorage.getItem('theme');
  if (saved === 'light' || saved === 'dark') return saved;
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
};

const useTheme = () => {
  const [theme, setTheme] = useState(getInitialTheme);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
    localStorage.setItem('theme', theme);
  }, [theme]);
  return { theme, setTheme };
};

const ThemeToggle = ({ theme, setTheme }) => (
  <button
    aria-label="Switch theme"
    title={theme === 'dark' ? 'تغییر به حالت روز' : 'تغییر به حالت شب'}
    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
  >
    {theme === 'dark' ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-gray-600" />}
  </button>
);

// ====== Live ticker from existing APIs ======
const useLiveTicker = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [cg, gold, fx] = await Promise.all([
          fetch(`${API}/crypto/grow/`).then(r => r.json()),
          fetch(`${API}/gold/api`).then(r => r.json()),
          fetch(`${API}/arz/api`).then(r => r.json()),
        ]);

        const bySym = Object.fromEntries(cg.map((x) => [x.symbol, x]));
        const btc = bySym['BTCUSDT'];
        const eth = bySym['ETHUSDT'];

        const usd = pick(fx.currencies, 'USD');
        const ounce = pick(gold.items, 'OUNCE');
        const gram18 = pick(gold.items, 'AYAR18');

        const arr = [];
        if (btc) arr.push({ name: 'بیت‌کوین', price: btc.price, change: Number(btc.diff), positive: pos(btc.diff) });
        if (eth) arr.push({ name: 'اتریوم', price: eth.price, change: Number(eth.diff), positive: pos(eth.diff) });
        if (ounce) arr.push({ name: 'اونس طلا', price: ounce.price, change: Number(ounce.percent_change), positive: pos(ounce.percent_change) });
        if (gram18) arr.push({ name: 'گرم طلا ۱۸', price: gram18.price, change: Number(gram18.percent_change), positive: pos(gram18.percent_change) });
        if (usd) arr.push({ name: 'دلار آمریکا', price: usd.price, change: Number(usd.percent_change), positive: pos(usd.percent_change) });

        if (alive) setItems(arr);
      } catch (e) {
        console.error(e);
      }
    };

    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return items;
};

const LivePriceTicker = () => {
  const prices = useLiveTicker();
  const doubled = useMemo(() => prices.concat(prices), [prices]);

  return (
    <div className="bg-blue-50 dark:bg-gray-800/50 backdrop-blur-sm p-3 overflow-hidden border-b border-blue-100 dark:border-gray-700">
      <div className="flex animate-marquee whitespace-nowrap">
        {doubled.map((item, i) => (
          <div key={i} className="flex items-center mx-6">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}:</span>
            <span className="text-sm font-mono mx-2 text-gray-900 dark:text-white">{item.price}</span>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              item.positive 
                ? 'text-emerald-700 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30' 
                : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
            }`}>
              {pct(item.change)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketCard = ({ title, description, imgSrc, liveData, link, icon }) => (
  <div className="group relative bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-3xl shadow-lg hover:shadow-2xl overflow-hidden transform hover:-translate-y-1 transition-all duration-500">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    
    <div className="relative">
      <div className="h-48 overflow-hidden">
        <img src={imgSrc} alt={title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
        <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 rounded-full p-2 backdrop-blur-sm">
          {icon}
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{title}</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">{description}</p>

        <div className="space-y-3 mb-6">
          {(liveData || []).map(item => (
            <div key={item.name} className="flex justify-between items-center p-3 bg-gray-50/50 dark:bg-gray-700/30 rounded-xl">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-gray-900 dark:text-white">{item.price}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  item.positive 
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' 
                    : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                }`}>
                  {pct(item.change)}
                </span>
              </div>
            </div>
          ))}
        </div>

        <a 
          href={link} 
          className="group/btn relative inline-flex items-center justify-center w-full px-6 py-4 font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden"
        >
          <span className="relative z-10 flex items-center gap-2">
            مشاهده بازار
            <TrendingUp className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
        </a>
      </div>
    </div>
  </div>
);

export default function HomePage() {
  const [goldLive, setGoldLive] = useState([]);
  const [fxLive, setFxLive] = useState([]);
  const [cryptoLive, setCryptoLive] = useState([]);
  const { theme, setTheme } = useTheme();

  // Add CSS for marquee animation to head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes marquee {
        0% { transform: translateX(0%); }
        100% { transform: translateX(-50%); }
      }
      .animate-marquee {
        animation: marquee 40s linear infinite;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadGold = async () => {
      try {
        const data = await fetch(`${API}/gold/api`).then(r => r.json());
        const top = pick3(data.items, ['EMAMI', 'AYAR18', 'OUNCE'], (k) => ({ EMAMI: 'سکه امامی', AYAR18: 'گرم طلا ۱۸', OUNCE: 'اونس جهانی' }[k] || k));
        if (alive) setGoldLive(top);
      } catch (e) { console.error(e); }
    };

    const loadFx = async () => {
      try {
        const data = await fetch(`${API}/arz/api`).then(r => r.json());
        const top = pick3(data.currencies, ['USD', 'EUR', 'AED'], (k) => ({ USD: 'دلار آمریکا', EUR: 'یورو', AED: 'درهم امارات' }[k] || k));
        if (alive) setFxLive(top);
      } catch (e) { console.error(e); }
    };

    const loadCrypto = async () => {
      try {
        const url = `${API}/crypto/fav/?parameter=BTCUSDT,ETHUSDT,BNBUSDT`;
        const arr = await fetch(url).then(r => r.json());

        const top = arr.map(x => ({
          symbol: x.symbol,
          name: x.symbol.replace('USDT', ''),
          price: x.price,
          change: Number(x.diff),
          positive: Number(x.diff) >= 0,
          icon: `${API}/images/${x.symbol}.png`,
        }));

        setCryptoLive(top);
      } catch (e) {
        console.error(e);
      }
    };

    const loadAll = () => { loadGold(); loadFx(); loadCrypto(); };
    loadAll();

    const id = setInterval(loadAll, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 text-gray-900 dark:text-white min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">قیمت بازار</div>
          </div>
          
          <div className="flex items-center gap-4">
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>
        </div>
        <LivePriceTicker />
      </header>

      <main className="pt-32">
        {/* Hero */}
        <section className="relative py-20 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/5 to-indigo-600/10"></div>
          <div className="container mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-sm font-medium mb-6">
              <Globe className="w-4 h-4" />
              پلتفرم معاملات و قیمت‌گذاری
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                دسترسی لحظه‌ای
              </span>
              <br />
              <span className="text-gray-900 dark:text-white">به نبض بازارهای مالی</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto mb-10 leading-relaxed">
              قیمت‌های دقیق و بدون وقفه ارز دیجیتال، طلا، سکه و ارزهای جهانی در یک پلتفرم حرفه‌ای. 
              <br className="hidden md:block" />
              تجربه‌ای نوین از دنیای مالی با رابط کاربری مدرن و سریع.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 transform hover:-translate-y-1">
                <span className="relative z-10 flex items-center gap-2">
                  شروع معاملات
                  <TrendingUp className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              </button>
              
              <button className="group px-8 py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-300">
                <span className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  دانلود اپلیکیشن
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Markets */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">بازارهای مالی</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                دسترسی به کامل‌ترین مجموعه داده‌های مالی با بالاترین دقت و سرعت
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <MarketCard
                title="بازار کریپتو"
                description="قیمت‌های لحظه‌ای از منابع شما با پوشش کامل ارزهای دیجیتال محبوب و تحلیل‌های تخصصی."
                imgSrc="https://tabdeal.org/academy/wp-content/uploads/2023/02/1.jpg"
                link="/cr/1"
                liveData={cryptoLive}
                icon={<TrendingUp className="w-5 h-5 text-indigo-600" />}
              />
              <MarketCard
                title="طلا و سکه"
                description="نرخ انواع سکه و طلا در بازار آزاد ایران با تحلیل روندها و پیش‌بینی قیمت‌ها."
                imgSrc="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQJRt-NB_lS4LK175-ePJkYFbK05wL3T9N5uhitPbzkNQ&s-1589779390235-9646a73af634?auto=format&fit=crop&q=85&w=600"
                link="/gold"
                liveData={goldLive}
                icon={<Award className="w-5 h-5 text-yellow-600" />}
              />
              <MarketCard
                title="ارزهای بازار آزاد"
                description="قیمت روز ارزهای مهم دنیا در بازار آزاد با نمودارهای تحلیلی و اخبار مرتبط."
                imgSrc="https://media.hamshahrionline.ir/d/2022/06/20/4/4689876.jpg?ts=1655707533000&fit=crop&q=85&w=600"
                link="/arz"
                liveData={fxLive}
                icon={<Globe className="w-5 h-5 text-green-600" />}
              />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-gray-800/50 dark:to-gray-700/50">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">پلتفرمی که می‌توانید به آن اعتماد کنید</h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-16">
              ما بهترین ابزارها و دقیق‌ترین داده‌ها را برای موفقیت شما فراهم کرده‌ایم. 
              تجربه‌ای حرفه‌ای با امنیت بالا و رابط کاربری مدرن.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <Feature 
                icon={<Zap className="h-12 w-12 text-yellow-500" />} 
                title="داده‌های لحظه‌ای"
                description="به‌روزرسانی قیمت‌ها با کمترین تاخیر ممکن و دقت بالا از معتبرترین منابع جهان."
              />
              <Feature 
                icon={<Shield className="h-12 w-12 text-blue-500" />} 
                title="قابل اعتماد و شفاف"
                description="قیمت‌ها از منابع معتبر دریافت می‌شوند و تمام فرآیندها کاملاً شفاف و قابل پیگیری هستند."
              />
              <Feature 
                icon={<Award className="h-12 w-12 text-purple-500" />} 
                title="تجربه کاربری برتر"
                description="طراحی مدرن و کاربرپسند با امکانات پیشرفته برای تحلیل و پیگیری بازارها."
              />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 mt-20">
        <div className="container mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">قیمت بازار</div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                مرجع قیمت‌های دقیق و لحظه‌ای بازارهای مالی ایران. 
                پلتفرمی مطمئن برای دنبال کردن روند بازارها.
              </p>
              <div className="flex gap-4">
                <button className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors">
                  <Globe className="w-5 h-5 text-gray-600 dark:text-gray-400 mx-auto" />
                </button>
              </div>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-gray-900 dark:text-white">دسترسی سریع</h5>
              <ul className="space-y-3">
                <li><a href="/cr/1" className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">بازار کریپتو</a></li>
                <li><a href="/gold" className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">بازار طلا و سکه</a></li>
                <li><a href="/arz" className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">بازار ارز</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-gray-900 dark:text-white">شرکت</h5>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">درباره ما</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">تماس با ما</a></li>
                <li><a href="#" className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">قوانین و مقررات</a></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <p className="text-gray-500 dark:text-gray-400">&copy; {new Date().getFullYear()} قیمت بازار. تمامی حقوق محفوظ است.</p>
              <span className="text-sm text-gray-500 dark:text-gray-400">ساخته شده با ❤️ برای ایران</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Feature = ({ icon, title, description }) => (
  <div className="group flex flex-col items-center text-center p-6 bg-white/60 dark:bg-gray-800/60 rounded-2xl hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-300 hover:shadow-xl">
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 p-6 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
      {icon}
    </div>
    <h4 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{title}</h4>
    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
  </div>
);